import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import ConfirmModal from '../components/ConfirmModal';
import Modal from '../components/Modal';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  BookOpen,
  Droplets,
  Heart,
  UserCheck,
  Calendar
} from 'lucide-react';

const MissionsAdmin = () => {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    id: '',
    order: 0,
    type: 'course',
    requirements: [],
    buttonText: 'Iniciar',
    buttonType: 'disabled',
    badgeText: 'Em breve',
    icon: 'CheckCircle',
    active: true
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [missionToDelete, setMissionToDelete] = useState(null);

  const iconOptions = [
    { value: 'CheckCircle', label: 'Check Circle', icon: CheckCircle },
    { value: 'UserCheck', label: 'User Check', icon: UserCheck },
    { value: 'BookOpen', label: 'Book Open', icon: BookOpen },
    { value: 'Droplets', label: 'Droplets', icon: Droplets },
    { value: 'Heart', label: 'Heart', icon: Heart },
    { value: 'Calendar', label: 'Calendar', icon: Calendar },
    { value: 'AlertCircle', label: 'Alert Circle', icon: AlertCircle }
  ];

  const typeOptions = [
    { value: 'profile', label: 'Perfil' },
    { value: 'course', label: 'Curso' },
    { value: 'sacrament', label: 'Sacramento' },
    { value: 'event', label: 'Evento' }
  ];

  const buttonTypeOptions = [
    { value: 'active', label: 'Ativo' },
    { value: 'disabled', label: 'Desabilitado' },
    { value: 'badge', label: 'Badge' }
  ];

  useEffect(() => {
    if (user?.role !== 'administracao' && user?.role !== 'pastor') {
      setError('Acesso negado. Apenas administradores podem gerenciar missões.');
      return;
    }
    loadMissions();
  }, [user]);

  const loadMissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/missions/admin');
      setMissions(response.data);
    } catch (error) {
      console.error('Erro ao carregar missões:', error);
      setError('Erro ao carregar missões');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      if (editingMission) {
        await axios.put(`/api/missions/${editingMission._id}`, formData);
        setSuccess('Missão atualizada com sucesso!');
      } else {
        await axios.post('/api/missions', formData);
        setSuccess('Missão criada com sucesso!');
      }

      setShowModal(false);
      setEditingMission(null);
      resetForm();
      loadMissions();
    } catch (error) {
      console.error('Erro ao salvar missão:', error);
      setError(error.response?.data?.message || 'Erro ao salvar missão');
    }
  };

  const handleEdit = (mission) => {
    setEditingMission(mission);
    setFormData({
      title: mission.title,
      description: mission.description,
      id: mission.id,
      order: mission.order,
      type: mission.type,
      requirements: mission.requirements || [],
      buttonText: mission.buttonText,
      buttonType: mission.buttonType,
      badgeText: mission.badgeText,
      icon: mission.icon,
      active: mission.active
    });
    setShowModal(true);
  };

  const handleDelete = (mission) => {
    setMissionToDelete(mission);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!missionToDelete) return;

    try {
      await axios.delete(`/api/missions/${missionToDelete._id}`);
      setSuccess('Missão excluída com sucesso!');
      loadMissions();
      setShowDeleteModal(false);
      setMissionToDelete(null);
    } catch (error) {
      console.error('Erro ao excluir missão:', error);
      setError(error.response?.data?.message || 'Erro ao excluir missão');
      setShowDeleteModal(false);
      setMissionToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      id: '',
      order: 0,
      type: 'course',
      requirements: [],
      buttonText: 'Iniciar',
      buttonType: 'disabled',
      badgeText: 'Em breve',
      icon: 'CheckCircle',
      active: true
    });
  };

  const handleNewMission = () => {
    setEditingMission(null);
    resetForm();
    setShowModal(true);
  };

  const getIconComponent = (iconName) => {
    const iconOption = iconOptions.find(opt => opt.value === iconName);
    return iconOption ? iconOption.icon : CheckCircle;
  };

  if (user?.role !== 'administracao' && user?.role !== 'pastor') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="card">
          <div className="card-body text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-600 mb-2">Acesso Negado</h2>
            <p className="text-secondary">Apenas administradores podem gerenciar missões.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-primary">Administração de Missões</h1>
        <button
          onClick={handleNewMission}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nova Missão
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-color"></div>
          <p className="mt-2 text-secondary">Carregando missões...</p>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            {missions.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-secondary">Nenhuma missão cadastrada</p>
                <button
                  onClick={handleNewMission}
                  className="btn btn-primary mt-4"
                >
                  Criar primeira missão
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Ordem</th>
                      <th className="text-left py-3 px-4">Título</th>
                      <th className="text-left py-3 px-4">Tipo</th>
                      <th className="text-left py-3 px-4">Botão</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missions.map((mission) => {
                      const IconComponent = getIconComponent(mission.icon);
                      return (
                        <tr key={mission._id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <span className="font-medium">{mission.order}</span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <IconComponent className="w-5 h-5 text-primary-color" />
                              <div>
                                <p className="font-medium">{mission.title}</p>
                                <p className="text-sm text-secondary line-clamp-1">{mission.description}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                              {typeOptions.find(opt => opt.value === mission.type)?.label || mission.type}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              mission.buttonType === 'active' ? 'bg-green-100 text-green-800' :
                              mission.buttonType === 'disabled' ? 'bg-gray-100 text-gray-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {mission.buttonType === 'badge' ? mission.badgeText : mission.buttonText}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              mission.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {mission.active ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleEdit(mission)}
                                className="btn btn-sm btn-outline"
                                title="Editar"
                              >
                                <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline ml-1">Editar</span>
                              </button>
                              <button
                                onClick={() => handleDelete(mission)}
                                className="btn btn-sm btn-danger"
                                title="Excluir"
                              >
                                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline ml-1">Excluir</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Criar/Editar Missão */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingMission(null);
          resetForm();
        }}
        title={editingMission ? 'Editar Missão' : 'Nova Missão'}
        size="lg"
      >
        <div>
              <form id="mission-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Título *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID da Missão *
                    </label>
                    <input
                      type="text"
                      value={formData.id}
                      onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                      placeholder="ex: complete-profile"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ordem *
                    </label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                      min="0"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo *
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                      required
                    >
                      {typeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ícone
                    </label>
                    <select
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                    >
                      {iconOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo do Botão
                    </label>
                    <select
                      value={formData.buttonType}
                      onChange={(e) => setFormData({ ...formData, buttonType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                    >
                      {buttonTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {formData.buttonType === 'badge' ? 'Texto do Badge' : 'Texto do Botão'}
                    </label>
                    <input
                      type="text"
                      value={formData.buttonType === 'badge' ? formData.badgeText : formData.buttonText}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        [formData.buttonType === 'badge' ? 'badgeText' : 'buttonText']: e.target.value 
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-gray-700">
                    Missão ativa
                  </label>
                </div>

              </form>
              
              <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingMission(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  form="mission-form"
                  className="btn btn-primary"
                >
                  <Save className="w-4 h-4" />
                  {editingMission ? 'Atualizar' : 'Criar'}
                </button>
              </div>
        </div>
      </Modal>

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setMissionToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir a missão "${missionToDelete?.title}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="danger"
      />
    </div>
  );
};

export default MissionsAdmin;